// pkg135 — Not Invited / Awaiting Login split (D-135b): StatusBadge takes the client row and derives three states (Active = logged in; else invited_at ? Awaiting Login : Not Invited) — delivered 2026-07-28
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';


function lastName(name) {
  const parts = (name || '').trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : parts[0].toLowerCase();
}

export default function AdminClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('lastname');
  const [sortDir, setSortDir] = useState('asc');


  useEffect(() => {
    adminApi.listClients().
    then((data) => {setClients(data);setLoading(false);}).
    catch((err) => {setError(err.message);setLoading(false);});
  }, []);

  function toggleSort(col) {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');else
    {setSortBy(col);setSortDir('asc');}
  }

  // NOTE: Search runs against the real underlying values (name, email,
  // username) regardless of privacy mode. This is deliberate — you might
  // know who you're looking for by their real info even when names are
  // visually masked. The search input itself stays visible.
  const q = search.toLowerCase();
  const filtered = clients.
  filter((c) =>
  (c.name || '').toLowerCase().includes(q) ||
  (c.email || '').toLowerCase().includes(q) ||
  (c.display_name || '').toLowerCase().includes(q)
  ).
  sort((a, b) => {
    let va = sortBy === 'lastname' ? lastName(a.name) : new Date(a.created_at);
    let vb = sortBy === 'lastname' ? lastName(b.name) : new Date(b.created_at);
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // pkg135 — three-state badge (D-135b): Active = the client has logged in
  // and changed their password (covers hand-delivered credentials with no
  // auth changes); among not-yet-logged-in clients, invited_at from
  // /send-welcome splits the honest "Not Invited" from "Awaiting Login".
  function StatusBadge({ client }) {
    const state = !client.must_change_password
      ? { label: 'Active',        bg: '#f0faf5', fg: '#1e7e5e', bd: '#b7e3cf' }
      : client.invited_at
      ? { label: 'Awaiting Login', bg: '#fff8e1', fg: '#b7870a', bd: '#f5d87a' }
      : { label: 'Not Invited',    bg: '#f2f2f2', fg: '#666666', bd: '#d9d9d9' };
    return (
      <span style={{
        fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3,
        background: state.bg,
        color: state.fg,
        border: `1px solid ${state.bd}`,
        whiteSpace: 'nowrap'
      }}>
        {state.label}
      </span>);

  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12 }}>
        <div>
          <h1 className="page-title">All Clients</h1>
          <p className="page-subtitle" style={{ marginBottom: 0 }}>
            {clients.length} active client account{clients.length !== 1 ? 's' : ''}
            {' '}&middot;{' '}
            <Link to="/admin/closed-files" style={{ color: '#2f5496', textDecoration: 'none' }}>
              View Closed Files →
            </Link>
          </p>
        </div>
        <Link to="/admin/create-client" className="btn btn-primary btn-sm" style={{ marginTop: 8, flexShrink: 0 }}>+ New Client</Link>
      </div>

      {/* Search + sort controls */}
      <div style={{ display: 'flex', gap: 10, margin: '16px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 180 }}>
          <input
            type="text"
            placeholder="Search by name, email, or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ margin: 0 }} />
          
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button className={`btn btn-sm${sortBy === 'lastname' ? ' btn-primary' : ''}`}
          onClick={() => toggleSort('lastname')}>
            Last Name {sortBy === 'lastname' ? sortDir === 'asc' ? '↑' : '↓' : ''}
          </button>
          <button className={`btn btn-sm${sortBy === 'created' ? ' btn-primary' : ''}`}
          onClick={() => toggleSort('created')}>
            Date {sortBy === 'created' ? sortDir === 'asc' ? '↑' : '↓' : ''}
          </button>
          {search && <button className="btn btn-sm" onClick={() => setSearch('')}>Clear</button>}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', fontSize: 13 }}>Loading...</div>}

      {!loading && !error &&
      <>
          {search &&
        <p style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888', marginBottom: 12 }}>
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
            </p>
        }

          {filtered.length === 0 &&
        <div className="card" style={{ textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
              {search ? `No clients match "${search}".` : 'No clients yet.'}
            </div>
        }

          {/* Desktop table — visually matches Dashboard's Recent Clients
              table (dark header, white text, 9px/10px padding). The only
              differences from Dashboard:
                * iterates over `filtered` (search + sort result)
                * Name and Created headers are clickable for sorting
                * no Username column (visible only on the client detail page)
                * no hourly_rate badge in the Name cell
              The card wrapper carries a `client-list-table` marker class
              that index.css uses to enable horizontal scroll ONLY in the
              narrow 769–900px squish-zone where the table would otherwise
              wrap badly. At normal laptop widths no scrollbar appears. */}
          <div className="desktop-only">
            <div className="card client-list-table" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#0F1838' }}>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600, cursor: 'pointer' }} onClick={() => toggleSort('lastname')}>
                      Name {sortBy === 'lastname' ? sortDir === 'asc' ? '↑' : '↓' : <span style={{ color: '#888' }}>↕</span>}
                    </th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600 }}>Email</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600, cursor: 'pointer' }} onClick={() => toggleSort('created')}>
                      Created {sortBy === 'created' ? sortDir === 'asc' ? '↑' : '↓' : <span style={{ color: '#888' }}>↕</span>}
                    </th>
                    <th style={{ padding: '9px 14px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) =>
                <tr key={c.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '10px 14px', fontSize: 14, fontWeight: 500 }}>{c.name}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555' }}>{c.email}</td>
                      <td style={{ padding: '10px 14px' }}><StatusBadge client={c} /></td>
                      <td style={{ padding: '10px 14px', fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888', whiteSpace: 'nowrap' }}>
                        {new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <Link to={`/admin/clients/${c.id}`} className="btn btn-sm">Manage</Link>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards — hidden on desktop */}
          <div className="mobile-only">
            {filtered.map((c) =>
          <div key={c.id} className="card" style={{ padding: '16px', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#555', fontFamily: 'Arial, sans-serif', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>
                    <StatusBadge client={c} />
                  </div>
                  <Link to={`/admin/clients/${c.id}`} className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>
                    Manage
                  </Link>
                </div>
              </div>
          )}
          </div>
        </>
      }
    </div>);

}